<script lang="ts" setup>
import type { Slot } from 'vue'
import { getCurrentInstance, nextTick, ref, useTemplateRef, watch } from 'vue'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import Button from '~/components/ui/Button.vue'

const { bgClass, customTrigger } = defineProps<{ bgClass?: string, customTrigger?: boolean }>()

defineSlots<{
  default?: (props: { isFullscreen: boolean, open?: () => void }) => Slot
  open?: (props: { open: () => void }) => Slot
  teleported?: (props: { isFullscreen: boolean, open?: () => void }) => Slot
}>()

const open = ref(false)

const triggerEl = useTemplateRef('trigger')
const dialogEl = useTemplateRef('dialog')
const id = `fullscreen-${getCurrentInstance()?.uid.toString()}`

watch(open, (value) => {
  nextTick(() => {
    if (value) {
      dialogEl.value?.showModal()
    } else {
      dialogEl.value?.close()
      triggerEl.value?.focus()
    }
  })
})
</script>

<template>
  <div
    class="relative grid auto-rows-max grid-rows-1"
    v-bind="$attrs"
  >
    <slot
      v-if="!customTrigger"
      :open="() => (open = true)"
      name="open"
    >
      <button
        ref="trigger"
        :aria-label="$t('open')"
        class="group z-1 col-start-1 row-start-1 grid size-full cursor-pointer place-items-center focus-visible:ring"
        @click="() => open = true"
      />
    </slot>

    <slot
      :is-fullscreen="open"
      :open="() => (open = !open)"
    />

    <teleport
      v-if="$slots.teleported"
      :disabled="!open"
      :to="`#${id}`"
      defer
    >
      <slot
        :is-fullscreen="open"
        :open="() => (open = true)"
        name="teleported"
      />
    </teleport>
  </div>

  <dialog
    ref="dialog"
    class="fullscreen-content-dialog"
    @close="open = false"
  >
    <div
      :class="bgClass || 'bg-(--theme-5)'"
      class="!fixed inset-0 z-10 grid size-full auto-rows-max grid-rows-1 overscroll-none"
    >
      <slot :is-fullscreen="open" />
      <div v-if="$slots.teleported" :id />

      <Button
        v-focus
        :aria-label="$t('close')"
        class="absolute top-4 right-8"
        color="white"
        shape="square"
        size="xl"
        @click="open = false"
      >
        <font-awesome-icon :icon="faXmark" />
      </Button>
    </div>
  </dialog>
</template>

<style>
html:has(.fullscreen-content-dialog[open]) {
  overflow: hidden;
}
</style>
